import { createRoot } from 'react-dom/client'

/**
 * Clase base para crear controles de Mapbox que pueden renderizar componentes React
 * Mantiene compatibilidad con Redux y el ecosistema React
 */
class BaseMapControl {
  constructor(options = {}) {
    this._container = null
    this._root = null
    this._map = null
    this._cleanupTimeoutId = null
    this.options = options
  }

  /**
   * Método llamado por Mapbox al agregar el control
   * @param {mapboxgl.Map} map - Instancia del mapa
   * @returns {HTMLElement} - Elemento DOM del control
   */
  onAdd(map) {
    if (this._cleanupTimeoutId) {
      clearTimeout(this._cleanupTimeoutId)
      this._cleanupTimeoutId = null
    }

    this._map = map
    this._container = document.createElement('div')
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
    this._container.style.width = '30px'
    this._container.style.height = '30px'

    // Aplicar estilos personalizados si existen
    if (this.options.className) {
      this._container.classList.add(this.options.className)
    }

    // Crear root de React 18
    this._root = createRoot(this._container)

    // Renderizar el componente React
    this._renderContent()

    return this._container
  }

  /**
   * Método llamado por Mapbox al remover el control
   * ✅ Unmount diferido para evitar carrera con render de React
   * y remover el nodo DOM solo después de desmontar el árbol.
   */
  onRemove() {
    const root = this._root
    const container = this._container

    // Cortar referencias de inmediato para evitar renders posteriores.
    this._root = null
    this._container = null
    this._map = null

    const cleanup = () => {
      this._cleanupTimeoutId = null
      try {
        root?.unmount()
      } catch (_) {}
      try {
        container?.remove()
      } catch (_) {}
    }

    if (this._cleanupTimeoutId) {
      clearTimeout(this._cleanupTimeoutId)
    }

    // Usar macrotask (no microtask) evita "Attempted to synchronously unmount a root
    // while React was already rendering" en escenarios de unmount concurrente.
    this._cleanupTimeoutId = setTimeout(cleanup, 0)
  }

  /**
   * Método para actualizar el contenido renderizado
   * Debe ser sobrescrito por las clases hijas
   */
  _renderContent() {
    console.warn('_renderContent debe ser implementado por la clase hija')
  }

  /**
   * Método público para forzar re-renderizado
   */
  update() {
    if (this._root && this._container) {
      this._renderContent()
    }
  }

  /**
   * Obtener la instancia del mapa
   */
  getMap() {
    return this._map
  }
}

export default BaseMapControl
